import axios from "axios";

// Keep local development usable even when an .env file has not been created.
// Production should set REACT_APP_BACKEND_URL to the deployed API origin.
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// --- Token-based auth (localStorage / sessionStorage / cookie) for cross-domain deployments ---
const ACCESS_KEY = "cas_access_token";
const REFRESH_KEY = "cas_refresh_token";
/** ~7 days access cookie; refresh cookie aligns with backend REFRESH_TOKEN_DAYS (30). */
const ACCESS_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const REFRESH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
/** In-memory fallback when Chrome blocks storage (SecurityError on Window.localStorage). */
const memoryStore = new Map();

let refreshPromise = null;

function withStorage(kind, fn) {
  try {
    // Must touch storage inside try — passing localStorage as an arg throws before catch.
    const storage = kind === "session" ? window.sessionStorage : window.localStorage;
    return fn(storage);
  } catch {
    return undefined;
  }
}

function readCookie(name) {
  try {
    const esc = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = document.cookie.match(new RegExp(`(?:^|; )${esc}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name, value, maxAgeSec) {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function clearCookie(name) {
  try {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function readPersisted(key) {
  const fromLocal = withStorage("local", (s) => s.getItem(key));
  if (fromLocal) return fromLocal;
  const fromSession = withStorage("session", (s) => s.getItem(key));
  if (fromSession) return fromSession;
  const fromCookie = readCookie(key);
  if (fromCookie) {
    memoryStore.set(key, fromCookie);
    return fromCookie;
  }
  return memoryStore.get(key) || null;
}

function writePersisted(key, value, { remember = true, maxAge = ACCESS_COOKIE_MAX_AGE } = {}) {
  memoryStore.set(key, value);
  writeCookie(key, value, maxAge);
  if (remember) {
    withStorage("local", (s) => { s.setItem(key, value); });
    withStorage("session", (s) => { s.removeItem(key); });
  } else {
    withStorage("session", (s) => { s.setItem(key, value); });
    withStorage("local", (s) => { s.removeItem(key); });
  }
}

function removePersisted(key) {
  memoryStore.delete(key);
  clearCookie(key);
  withStorage("local", (s) => { s.removeItem(key); });
  withStorage("session", (s) => { s.removeItem(key); });
}

export function getStoredToken() {
  return readPersisted(ACCESS_KEY);
}

export function getStoredRefreshToken() {
  return readPersisted(REFRESH_KEY);
}

/** @param {string} token @param {{ remember?: boolean }} [opts] */
export function setStoredToken(token, opts = {}) {
  const remember = opts.remember !== false;
  writePersisted(ACCESS_KEY, token, { remember, maxAge: ACCESS_COOKIE_MAX_AGE });
}

/** @param {string} token @param {{ remember?: boolean }} [opts] */
export function setStoredRefreshToken(token, opts = {}) {
  if (!token) return;
  const remember = opts.remember !== false;
  writePersisted(REFRESH_KEY, token, { remember, maxAge: REFRESH_COOKIE_MAX_AGE });
}

/** @param {{ access_token?: string, refresh_token?: string }} tokens @param {{ remember?: boolean }} [opts] */
export function setAuthTokens(tokens, opts = {}) {
  if (tokens?.access_token) setStoredToken(tokens.access_token, opts);
  if (tokens?.refresh_token) setStoredRefreshToken(tokens.refresh_token, opts);
}

export function clearStoredToken() {
  removePersisted(ACCESS_KEY);
  removePersisted(REFRESH_KEY);
}

/** Call before window.location OAuth redirects so the session survives the round-trip. */
export function persistAuthForExternalRedirect() {
  const access = getStoredToken();
  const refresh = getStoredRefreshToken();
  if (access) setStoredToken(access, { remember: true });
  if (refresh) setStoredRefreshToken(refresh, { remember: true });
}

/** Safe localStorage helpers for UI prefs (Remember me, etc.). */
export function safeLocalGet(key, fallback = null) {
  const v = withStorage("local", (s) => s.getItem(key));
  if (v != null) return v;
  return memoryStore.has(key) ? memoryStore.get(key) : fallback;
}

export function safeLocalSet(key, value) {
  memoryStore.set(key, value);
  withStorage("local", (s) => { s.setItem(key, value); });
}

export function safeLocalRemove(key) {
  memoryStore.delete(key);
  withStorage("local", (s) => { s.removeItem(key); });
}

/** Exchange refresh token for a new access (+ refresh) pair. Dedupes concurrent callers. */
export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refresh = getStoredRefreshToken();
    if (!refresh) throw new Error("No refresh token");
    const { data } = await axios.post(
      `${API_BASE}/api/auth/refresh`,
      { refresh_token: refresh },
      { headers: { "Content-Type": "application/json" } },
    );
    if (!data?.access_token) throw new Error("Refresh returned no access_token");
    setAuthTokens(data, { remember: true });
    return data.access_token;
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

// Attach Bearer token to every request if available
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401s — try refresh once, then retry original request
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err?.config;
    if (
      err?.response?.status === 401 &&
      config &&
      !config._retry &&
      !config.url?.includes("/auth/login") &&
      !config.url?.includes("/auth/refresh")
    ) {
      config._retry = true;
      try {
        const access = await refreshAccessToken();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${access}`;
        return api.request(config);
      } catch (e) {
        clearStoredToken();
      }
    }
    return Promise.reject(err);
  },
);

export const API_URL = API_BASE;
export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
